import type { DocumentRegistryRow } from '../../../store/api/documentRegistryApi';
import {
  BUSINESS_DOCUMENT_DEFINITIONS,
  type BusinessDocumentDefinition,
} from '../../../utils/businessDocumentDefinitions';
import { resolveDocumentTypeLabel, resolveDocumentUserFacingFileName } from '../../../utils/documentDisplayName';
import { resolveDocumentStatusLabel } from '../../../utils/documentStatusDisplay';
import type { DocumentSelectionRequest, SelectedDocument } from './documentSelectionTypes';

const TN_TTN_ITEM_KEY = 'CLIENT_TN_TTN_PACKAGE';

const CARRIER_LEGACY_TYPES = new Set([
  'CARRIER_INVOICE',
  'CARRIER_CONTRACT_REQUEST',
  'LOADING_TN',
  'UNLOADING_DOC',
  'LOADING_PHOTO',
  'UNLOADING_PHOTO',
  'RAIL_WAYBILL',
  'AWB',
  'ORIGINAL_TN',
]);

function normalizeToken(value?: string | null): string {
  return String(value || '').trim().toUpperCase();
}

export function isManualCompositionItemKey(itemKey: string): boolean {
  return itemKey.startsWith('MANUAL_');
}

function findDefinitionByKey(itemKey: string): BusinessDocumentDefinition | undefined {
  return BUSINESS_DOCUMENT_DEFINITIONS.find((definition) => definition.key === itemKey);
}

function buildDefinitionLegacyTypes(definition: BusinessDocumentDefinition): string[] {
  return [definition.legacyDocumentType].map(normalizeToken);
}

function matchesDefinitionOwnership(
  document: DocumentRegistryRow,
  definition: BusinessDocumentDefinition,
  routeIds: Set<number>
): boolean {
  if (definition.party === 'CLIENT') {
    if (definition.key === TN_TTN_ITEM_KEY) {
      return (
        document.application?.id != null ||
        (document.route?.id != null && routeIds.has(document.route.id))
      );
    }
    return document.application?.id != null && document.route == null;
  }

  return document.route?.id != null && routeIds.has(document.route.id);
}

/** Mirrors Backend `evaluateDocumentDefinitionMatch` ownership + type rules (FE-005A plan §5.3). */
export function documentMatchesCompositionSlot(
  document: DocumentRegistryRow,
  itemKey: string,
  routeId: number | null
): boolean {
  if (isManualCompositionItemKey(itemKey)) {
    return isClientSelectableDocument(document);
  }

  const definition = findDefinitionByKey(itemKey);
  if (!definition) {
    return false;
  }

  const routeIds =
    itemKey === TN_TTN_ITEM_KEY && routeId != null
      ? new Set<number>([routeId])
      : new Set<number>();

  const legacy = normalizeToken(document.legacyType || document.documentType);
  const legacyTypes = buildDefinitionLegacyTypes(definition);
  const sameCatalog = document.catalogCode === definition.catalogCode;
  const sameLegacy = legacyTypes.includes(legacy);

  if (!sameCatalog && !sameLegacy) {
    return false;
  }

  return matchesDefinitionOwnership(document, definition, routeIds);
}

function isClientSelectableDocument(document: DocumentRegistryRow): boolean {
  if (normalizeToken(document.sourceParty) === 'CARRIER') {
    return false;
  }

  const legacy = normalizeToken(document.legacyType || document.documentType);
  if (CARRIER_LEGACY_TYPES.has(legacy) && document.route?.id != null) {
    return false;
  }

  return document.application?.id != null || document.route?.id != null;
}

function belongsToApplication(document: DocumentRegistryRow, applicationId: number): boolean {
  if (document.application?.id === applicationId) {
    return true;
  }
  return document.application == null && document.route?.id != null;
}

function toSelectedDocument(
  document: DocumentRegistryRow,
  disabledDocumentIds: Set<number>
): SelectedDocument {
  const applicationLabel = document.application
    ? document.application.externalNumber || document.application.internalNumber
    : null;

  const typeLabel = resolveDocumentTypeLabel({
    documentType: document.documentType,
    customDocumentName: document.customDocumentName,
  });
  const lifecycleState =
    document.lifecycleState ?? document.documentEngine?.lifecycleState ?? null;

  const displayFileName = resolveDocumentUserFacingFileName({
    documentType: document.documentType,
    customDocumentName: document.customDocumentName,
    typeLabel: document.typeLabel,
    fileName: document.fileName,
  });

  return {
    documentId: document.id,
    fileName: displayFileName !== '—' ? displayFileName : document.fileName,
    documentType: document.documentType,
    typeLabel,
    documentNumber: document.documentNumber ?? null,
    documentDate: document.createdAt,
    lifecycleState,
    statusLabel: resolveDocumentStatusLabel(lifecycleState),
    counterpartyLabel:
      document.application?.clientName ?? document.route?.carrierName ?? null,
    routeLabel: document.route?.label ?? null,
    applicationLabel,
    disabledReason: disabledDocumentIds.has(document.id)
      ? 'Документ уже входит в комплект'
      : null,
    comment: document.comment ?? null,
    customDocumentName: document.customDocumentName ?? null,
  };
}

/** Filters registry rows for a composition slot — client-side only, no new Backend endpoints. */
export function filterDocumentsForCompositionPicker(
  documents: DocumentRegistryRow[],
  request: DocumentSelectionRequest
): SelectedDocument[] {
  const scope = request.selectionScope ?? 'slot';
  const disabledDocumentIds = new Set(request.disabledDocumentIds ?? []);

  if (scope === 'manual') {
    return documents
      .filter((document) => belongsToApplication(document, request.applicationId))
      .filter(isClientSelectableDocument)
      .map((document) => toSelectedDocument(document, disabledDocumentIds));
  }

  return documents
    .filter((document) => belongsToApplication(document, request.applicationId))
    .filter((document) => {
      if (request.excludeDocumentId != null && document.id === request.excludeDocumentId) {
        return false;
      }
      return documentMatchesCompositionSlot(document, request.itemKey, request.routeId);
    })
    .map((document) => toSelectedDocument(document, disabledDocumentIds));
}
